import axiosInstance from '@/services/axios.service';

function normalizePersonalId(id) {
  return id == null ? '' : String(id);
}

function hasPersonalData(personal) {
  const status = Number(personal?.status);
  return Boolean(
    personal
      && typeof personal === 'object'
      && !(Number.isFinite(status) && status >= 400)
      && Object.keys(personal).length > 0,
  );
}

export default {
  namespaced: true,

  state: () => ({
    byId: {},
    requestsById: {},
    list: [],
    listRequest: null,
    listMerchantId: null,
    listFetchedAt: 0,
  }),

  getters: {
    personalById: (state) => (id) => state.byId[normalizePersonalId(id)] || null,
    personalList: (state) => state.list,
  },

  mutations: {
    SET_PERSONAL(state, { id, personal }) {
      state.byId = {
        ...state.byId,
        [id]: personal,
      };
    },
    SET_LIST(state, list) {
      state.list = Array.isArray(list) ? list : [];
    },
    SET_LIST_META(state, { merchantId, fetchedAt } = {}) {
      state.listMerchantId = merchantId ?? null;
      state.listFetchedAt = Number(fetchedAt) || 0;
    },
    SET_LIST_REQUEST(state, request) {
      state.listRequest = request || null;
    },
    CLEAR_LIST_REQUEST(state) {
      state.listRequest = null;
    },
    SET_REQUEST(state, { id, request }) {
      state.requestsById = {
        ...state.requestsById,
        [id]: request,
      };
    },
    CLEAR_REQUEST(state, id) {
      const { [id]: removed, ...requestsById } = state.requestsById;
      state.requestsById = requestsById;
    },
    CLEAR_PERSONAL(state, id) {
      const { [id]: removedPersonal, ...byId } = state.byId;
      const { [id]: removedRequest, ...requestsById } = state.requestsById;
      state.byId = byId;
      state.requestsById = requestsById;
    },
    CLEAR_ALL(state) {
      state.byId = {};
      state.requestsById = {};
      state.list = [];
      state.listRequest = null;
      state.listMerchantId = null;
      state.listFetchedAt = 0;
    },
  },

  actions: {
    async fetchList({ commit, rootState, state }, options = {}) {
      const { force = false, ttlMs = 5 * 60 * 1000 } = options || {};
      const merchantId = rootState?.login?.merchant?.merchantId || rootState?.login?.merchant?.id || 'merchant';
      const now = Date.now();
      const fresh =
        Boolean(state.listMerchantId && state.listMerchantId === merchantId)
        && Boolean(state.listFetchedAt && now - state.listFetchedAt < ttlMs)
        && Array.isArray(state.list)
        && state.list.length > 0;

      if (!force && fresh) {
        return state.list;
      }

      if (!force && state.listRequest) {
        return state.listRequest;
      }

      const request = axiosInstance
        .get('/operator/personal/list')
        .then((response) => {
          const list = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
          commit('SET_LIST', list);
          commit('SET_LIST_META', { merchantId, fetchedAt: Date.now() });
          list.forEach((personal) => {
            const id = normalizePersonalId(personal?.id);
            if (!id) return;
            commit('SET_PERSONAL', { id, personal });
          });
          return list;
        })
        .catch((error) => {
          console.error('Error fetching personals:', error);
          return { status: 400, message: error };
        })
        .finally(() => {
          commit('CLEAR_LIST_REQUEST');
        });

      commit('SET_LIST_REQUEST', request);
      return request;
    },
    async fetchById({ state, commit }, { id, force = false } = {}) {
      const personalId = normalizePersonalId(id);
      if (!personalId) {
        return null;
      }

      const cachedPersonal = state.byId[personalId] || null;
      if (!force && cachedPersonal) {
        return cachedPersonal;
      }

      if (!force && state.requestsById[personalId]) {
        return state.requestsById[personalId];
      }

      const request = axiosInstance
        .get(`/operator/personal/read/${personalId}`)
        .then((personal) => {
          if (!hasPersonalData(personal)) {
            return cachedPersonal;
          }

          commit('SET_PERSONAL', { id: personalId, personal });
          return personal;
        })
        .catch((error) => {
          console.error('Error posting data=getUserById=>:', error);
          return { status: 400, message: error };
        })
        .finally(() => {
          commit('CLEAR_REQUEST', personalId);
        });

      commit('SET_REQUEST', { id: personalId, request });

      return request;
    },

    clearById({ commit }, id) {
      if (!id) return;
      commit('CLEAR_PERSONAL', id);
    },

    clearAll({ commit }) {
      commit('CLEAR_ALL');
    },
  },
};
